/**
 * PaywallPrompt — modal shown when a user hits a 402 (insufficient credits) or
 * clicks the low-balance badge.
 *
 * The dialog wraps the full CheckoutPage rather than duplicating pricing
 * markup — single source of truth for price/credit display.
 *
 * Close behaviour: clicking outside / pressing Esc dismisses the prompt.
 * The underlying failing request doesn't auto-retry; the caller (or the user
 * clicking the button again) is responsible for re-triggering the action.
 */
import React from 'react';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CheckoutPage from './CheckoutPage';
import { useBilling } from '../../context/BillingContext';

function reasonMessage(reason, detail) {
  if (detail?.message) return detail.message;
  switch (reason) {
    case 'empty_balance':
      return "You're out of EIS Credits. Top up to keep using AI features.";
    case 'low_balance':
      return 'Your balance is running low. Consider topping up before your next AI action.';
    case 'billing_inactive':
      return 'Your subscription is not active. Resolve billing to continue.';
    case 'insufficient_credits':
    default:
      return "You don't have enough credits for this action.";
  }
}

export default function PaywallPrompt() {
  const { paywallOpen, paywallReason, closePaywall } = useBilling();

  // paywallReason can be a string (from openPaywall) or an object when
  // triggered by an axios interceptor passing the 402 response body.
  const reasonKey = typeof paywallReason === 'string' ? paywallReason : paywallReason?.error;
  const message = reasonMessage(reasonKey, paywallReason);

  return (
    <Dialog
      open={paywallOpen}
      onClose={closePaywall}
      fullWidth
      maxWidth="md"
      aria-labelledby="paywall-title"
    >
      <DialogTitle id="paywall-title" sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
        Upgrade to continue
        <IconButton
          aria-label="Close"
          onClick={closePaywall}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity={reasonKey === 'empty_balance' ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {message}
        </Alert>
        <CheckoutPage onCancel={closePaywall} />
      </DialogContent>
    </Dialog>
  );
}
